import { create } from 'zustand';

/**
 * Multi-select state for messages. Scoped to the currently-open channel or
 * DM peer. Tied to a single `scope` key so opening a different channel
 * implicitly clears selection (we never carry it across views).
 */

export type SelectionScope = { kind: 'channel'; channelId: string } | { kind: 'dm'; userId: string };

interface SelectionState {
  active: boolean;
  scope: SelectionScope | null;
  ids: Set<string>;

  enter: (scope: SelectionScope, firstId?: string) => void;
  toggle: (id: string) => void;
  clear: () => void;
  exit: () => void;
}

function sameScope(a: SelectionScope | null, b: SelectionScope): boolean {
  if (!a) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'channel' && b.kind === 'channel') return a.channelId === b.channelId;
  if (a.kind === 'dm' && b.kind === 'dm') return a.userId === b.userId;
  return false;
}

export const useSelectionStore = create<SelectionState>((set) => ({
  active: false,
  scope: null,
  ids: new Set<string>(),

  enter: (scope, firstId) =>
    set(() => ({
      active: true,
      scope,
      ids: new Set(firstId ? [firstId] : []),
    })),

  toggle: (id) =>
    set((s) => {
      if (!s.active) return s;
      const next = new Set(s.ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // Если все убрали — выходим из режима
      if (next.size === 0) return { active: false, scope: null, ids: next };
      return { ids: next };
    }),

  clear: () => set({ ids: new Set() }),
  exit: () => set({ active: false, scope: null, ids: new Set() }),
}));

/** Helper: is the current selection scoped to this channel/dm? */
export function isSelectionFor(state: SelectionState, scope: SelectionScope): boolean {
  return state.active && sameScope(state.scope, scope);
}
